import { TIME_FORMAT } from 'constants/datetime';
import { EMPTY_ARRAY } from 'constants/defaults';
import { MILLISECONDS_PER_HOUR, MINUTE_HEIGHT_PX } from 'constants/hour';
import { TIMELY_GPS_TRACKER } from 'constants/known-apps';
import { TIMELINE_PX_OFFSET } from 'constants/memory';
import { DEFAULT_MEMORY_COLOR } from 'constants/timeline';
import { clamp, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { TimelineSortTypes } from 'redux/modules/timeline.types';
import type { AutoNoteStrategy } from 'types/hour';
import type {
  Entry,
  MemoriesGroupedByStatus,
  Memory,
  MemoryGroups,
  MemorySubEntry,
  MemoryWithTimespan,
  SuggestedEntryWithProjects,
} from 'types/timeline';
import { OperatorMode } from 'types/timeline';
import type { Duration } from 'types/units';
import type { UserTimeFormat } from 'types/user';
import { isContrastReadable } from 'utils/colors';
import { isAfter } from 'utils/date-helper';
import { calculateDuration } from 'utils/durations';
import { fromISO } from 'utils/luxon-helpers';
import { truncateText } from 'utils/text-helper';
import { extractAttributesFromEntry, extractUrlFromEntry } from 'utils/timeline';
import { keyBy, orderBy } from 'utils/typesafe';

/**
 * Returns total duration from an array of timestamps (from/to).
 */
export const combinedDurationOfMemories = <
  T extends {
    from: string;
    to: string;
  },
>(
  entries: Array<T> = []
): Duration => {
  const totalSeconds = entries
    // Get all entries as tuples representing the start/end times in seconds
    .map<[number, number]>(entry => {
      return [DateTime.fromISO(entry.from).toSeconds(), DateTime.fromISO(entry.to).toSeconds()];
    })

    // Sort the intervals by start time
    .sort(([aStart], [bStart]) => aStart - bStart)

    // Merge overlapping intervals
    .reduce<Array<[number, number]>>((stack, interval) => {
      if (!stack.length) {
        stack.push(interval);
        return stack;
      }

      const top = stack[stack.length - 1];
      if (top[1] < interval[0]) {
        stack.push(interval);
      } else if (top[1] < interval[1]) {
        top[1] = interval[1];
      }

      return stack;
    }, [])

    // Sum the seconds of all the intervals
    .reduce((sum, [start, end]) => sum + (end - start), 0);

  return calculateDuration(totalSeconds);
};

export const splitMemoryGroupsByStatus = (groups: MemoryGroups): MemoriesGroupedByStatus => {
  const all = groups;
  const selected: MemoryGroups = [];
  const other: MemoryGroups = [];
  const allMemories: Array<Memory> = [];
  const selectedMemories: Array<Memory> = [];
  const otherMemories: Array<Memory> = [];

  groups.forEach(item => {
    const isAtLeastPartiallySelected = item.isChecked || item.isPartlyChecked;
    const isNotFullySelected = !item.isChecked;
    const hasMemories = item.memories && item.memories.length > 0;

    // Don't include groups without memories
    if (!hasMemories) {
      return;
    }

    allMemories.push(...item.memories);

    // Anything that is (partially) selected goes in the "selected group"
    if (isAtLeastPartiallySelected) {
      selected.push(item);
      selectedMemories.push(...item.memories.filter(memory => memory.isChecked));
    }
    if (isNotFullySelected) {
      other.push(item);
      otherMemories.push(...item.memories.filter(memory => !memory.isChecked));
    }
  });

  return {
    all: {
      key: 'all',
      title: 'All memories',
      duration: combinedDurationOfMemories(allMemories),
      groups: all,
      showDisplayRulesDropdown: false,
    },
    selected: {
      // Not currently used. Might want to simplify this function, as we only use "other", and only
      // inside the hour editor right pane. Perhaps do this in the memory group factory?
      key: 'selected',
      title: 'To be logged',
      duration: combinedDurationOfMemories(selectedMemories),
      groups: selected,
      emptyText: `You haven't selected any memories to log yet.`,
      showDisplayRulesDropdown: false,
    },
    other: {
      key: 'other',
      title: 'Todays Memories',
      duration: combinedDurationOfMemories(otherMemories),
      groups: other,
      showDisplayRulesDropdown: true,
    },
    totals: {
      count: other.length + selected.length,
    },
  };
};

export const subEntryToMemoryFactory = (
  {
    id,
    entry_id,
    from,
    to,
    note,
  }: {
    id: number;
    entry_id: number;
    from: string;
    to: string;
    note: string;
  },
  options: { timeFormat: string }
): MemorySubEntry => {
  const startedAt = DateTime.fromISO(from);
  const endedAt = DateTime.fromISO(to);
  const secondsInDuration = endedAt.diff(startedAt, 'seconds');
  return {
    id,
    entry_id, // parent_id
    from,
    to,
    fromTime: startedAt.toFormat(options.timeFormat),
    toTime: endedAt.toFormat(options.timeFormat),
    note,
    duration: calculateDuration(secondsInDuration.seconds),
  };
};

export const cloneMemoryWithTimespan = (
  memory: Memory,
  options: {
    pixelsPerMinute?: number;
    isChecked: boolean;
    startOfTimeline: DateTime;
    endOfTimeline: DateTime;
    totalHours: number;
    color: string;
    timelinePxOffset?: number;
  }
): MemoryWithTimespan => {
  const {
    pixelsPerMinute = MINUTE_HEIGHT_PX,
    color,
    isChecked,
    startOfTimeline,
    endOfTimeline,
    totalHours,
    timelinePxOffset = TIMELINE_PX_OFFSET,
  } = options;

  const memoryFrom = DateTime.fromISO(memory.from);
  const memoryTo = DateTime.fromISO(memory.to);
  const from = memoryFrom < startOfTimeline ? startOfTimeline : memoryFrom;
  const to = memoryTo > endOfTimeline ? endOfTimeline : memoryTo;
  const isDotMemory = memory.from === memory.to;

  const minutesInDuration = to.diff(from, 'minutes').minutes;
  const hoursInDuration = Math.ceil(minutesInDuration / 60);
  const hoursFromStartOfTimeline = from.diff(startOfTimeline, 'hours').hours;

  // Determine position on vertical timeline.
  const hourHeightPx = 60 * pixelsPerMinute;
  // Rounded to avoid decimals in position -- causes blurriness
  const topPx =
    Math.round(
      (memoryFrom.diff(startOfTimeline).milliseconds / MILLISECONDS_PER_HOUR) * hourHeightPx
    ) + timelinePxOffset;

  const naiveHeightPx = Math.round(memory.duration.total_hours * hourHeightPx);

  // Make sure really long entries don't go off the bottom of the timeline, causing scroll issues.
  const timelineHeightPx = totalHours * 60 * pixelsPerMinute;
  const heightPx = clamp(naiveHeightPx, 0, timelineHeightPx - topPx + timelinePxOffset);
  const truncated = heightPx !== naiveHeightPx;

  // Move the card down, if the memory is truncated
  const cardOffsetTopPx = topPx < 0 ? Math.abs(topPx) : 0;

  // Scale minimum height across the different sizing options:
  const minHeight = isDotMemory ? 0 : 12 * pixelsPerMinute;
  return {
    ...memory,
    cacheBuster: [
      memory.cacheBuster,
      color,
      topPx,
      heightPx,
      cardOffsetTopPx,
      truncated,
      totalHours,
      hoursFromStartOfTimeline,
      hoursInDuration,
    ].join(),
    timespan: {
      parent_id: memory.id,
      color,
      isChecked,
      vertical: {
        topPx,
        heightPx: Math.max(minHeight, heightPx),
        cardOffsetTopPx,
        truncated,
      },
      grid: {
        cells: totalHours,
        start: hoursFromStartOfTimeline + 1,
        end: hoursFromStartOfTimeline + 1 + hoursInDuration,
      },
    },
  };
};

interface OptionProjectReference {
  id: number | null | undefined;
  color: string | null | undefined;
  name: string | null | undefined;
}

export interface EntryToMemoryFactoryOptions {
  selectedEntryIds: Array<number>;
  loggedEntryIds?: Array<number>;
  predictedEntryIds?: Array<number>;
  timelineMeta?: {
    fromDateTime: DateTime;
    toDateTime: DateTime;
    totalHours: number;
  };
  filter: {
    timeFormat: UserTimeFormat;
    expandedEntryIds: Array<number>;
  };
  entryIdToProjectRelations?: {
    [key: number]: OptionProjectReference;
  };
  currentHourDraftProject?: OptionProjectReference;
  entryIdsPredictedToHourDraftProject?: Array<number>;
  groupDuration?: Duration;
}

type BaseEntry = {
  id: number;
  from_timely?: boolean;
  title: string;
  tool?: {
    id?: string | null;
    raw?: string | null;
  } | null;
  context?: string | null;
};
interface BaseHourDraft {
  entry_ids?: Array<number>;
  timestamps?: Array<BaseTimestamp>;
}
interface BaseTimestamp {
  entry_ids: Array<string | number>;
}

export const extractNoteFromEntries = <T extends BaseEntry>(
  memories: Array<T>,
  timelyEntries: Array<{ id: number; title: string }>,
  draft: BaseHourDraft | null | undefined,
  strategy: AutoNoteStrategy = 'memory-titles',
  maxChars: number = 300
) => {
  if (strategy === 'memory-titles' && draft != null) {
    return extractNoteFromMemoryTitles(memories, timelyEntries, draft, maxChars);
  } else {
    return '';
  }
};

const extractNoteFromMemoryTitles = (
  memories: Array<BaseEntry>,
  timelyEntries: Array<{ id: number; title: string }>,
  draft: BaseHourDraft,
  maxChars: number = 300
): string => {
  const memoriesByTicId = keyBy(
    memories.filter(m => !m.from_timely),
    'id'
  );
  const memoriesByTimelyId = keyBy(
    memories.filter(m => m.from_timely),
    'id'
  );
  const entriesByTimelyId = keyBy(timelyEntries, 'id');

  const titles: Array<string> = [];
  const timestampEntryIds = uniq(draft.timestamps?.flatMap(t => t.entry_ids) || []);
  for (const entryIdRaw of timestampEntryIds) {
    if (!entryIdRaw) {
      continue;
    }
    // Sometimes entryId is a number for some reason. Defensive check here for now.
    const entryId = String(entryIdRaw);
    const splitIdx = entryId.lastIndexOf('_');
    if (splitIdx > 0) {
      const [tag, id] = [entryId.slice(0, splitIdx), entryId.slice(splitIdx + 1)];
      const entry = tag.includes('tic') ? memoriesByTicId[id] : memoriesByTimelyId[id];
      if (entry) {
        if (tag.startsWith('tool_') && entry.tool) {
          titles.push(entry.tool.raw || entry.tool.id || entry.title);
        } else if (tag.startsWith('context_') && entry.context) {
          titles.push(entry.context);
        } else {
          titles.push(entry.title);
        }
      }
    } else {
      const memory = memoriesByTicId[entryId] || memoriesByTimelyId[entryId];
      if (memory) {
        titles.push(memory.title);
      } else {
        const entry = entriesByTimelyId[entryId];
        if (entry) {
          titles.push(entry.title);
        }
      }
    }
  }

  if (titles.length === 0) {
    draft.entry_ids?.forEach(entryId => {
      const memory = memoriesByTimelyId[entryId];
      if (memory) {
        titles.push(memory.title);
      } else {
        const entry = entriesByTimelyId[entryId];
        if (entry) {
          titles.push(entry.title);
        }
      }
    });
  }

  const uniqueTitles = uniq(titles);
  const combinedNotes = uniqueTitles.join(' • ').replace(/(\r\n|\n|\r)/gm, '');

  if (combinedNotes.length > maxChars) {
    return truncateText(combinedNotes, maxChars);
  }

  return combinedNotes;
};

export const entryToMemoryFactory = (
  entry: Entry,
  options: EntryToMemoryFactoryOptions
): Memory => {
  const { filter, currentHourDraftProject } = options;

  const duration = entry.duration || calculateDuration(0);
  const attributes = extractAttributesFromEntry(entry.extra_attributes);
  const url = extractUrlFromEntry(entry);

  const isChecked = options.selectedEntryIds.includes(entry.id);

  const isPredicted = options.predictedEntryIds?.includes(entry.id) === true;
  const isLogged = options.loggedEntryIds?.includes(entry.id) === true;

  const isExpanded = options.filter && options.filter.expandedEntryIds.includes(entry.id);
  const isPredictedToCurrentProject =
    options.entryIdsPredictedToHourDraftProject?.includes(entry.id) === true;
  const projectReference =
    isChecked && currentHourDraftProject && currentHourDraftProject.id
      ? currentHourDraftProject
      : options.entryIdToProjectRelations?.[entry.id] || { id: null, color: null, name: null };

  const barColor = entry.color || DEFAULT_MEMORY_COLOR;

  const cardBgColor =
    isChecked && currentHourDraftProject && currentHourDraftProject.color
      ? currentHourDraftProject.color
      : (isLogged || isPredicted) && projectReference.color
        ? projectReference.color
        : null;

  const cardTextVariant = !cardBgColor
    ? 'dark'
    : isContrastReadable('#fff', cardBgColor)
      ? 'light'
      : 'dark';

  const fromToTimeFormat = TIME_FORMAT[filter.timeFormat];
  const subEntries = (entry.sub_entries || EMPTY_ARRAY).map(subEntry =>
    subEntryToMemoryFactory(subEntry, { timeFormat: fromToTimeFormat })
  );

  const isSpam = !!entry.spam;
  // This .at fallback should no longer be needed with updated entries API
  const from = DateTime.fromISO(entry.from);
  const to = DateTime.fromISO(entry.to);

  let icon = entry.icon_url;
  const fallbackIcon = entry.icon_fallback_url;

  const appAttribute = entry.extra_attributes?.find(a => a.name === 'application');
  if (appAttribute) {
    const application = encodeURIComponent(appAttribute.value);
    icon = `https://icons.timelyapp.com/apps/${getIconPlatform(
      entry.type
    )}/${application}?size=64&fallback=${encodeURIComponent(icon)}`;
  }

  if (url != null) {
    try {
      const hostname = new URL(url).hostname;
      if (hostname) {
        icon = `https://icons.timelyapp.com/favicons/${hostname}?size=64&fallback=${entry.icon_url}`;
      }
    } catch (e) {}
  }

  return {
    id: entry.id,
    from: entry.from,
    to: entry.to,
    icon,
    fallbackIcon,
    barColor,
    cardBgColor,
    cardTextVariant,
    project: projectReference,
    date: entry.date,
    fromTime: from.toFormat(fromToTimeFormat),
    toTime: to.toFormat(fromToTimeFormat),
    title: entry.title,
    url,
    description: entry.description,
    duration,
    type: entry.type,
    subEntries,
    isChecked,
    isLogged,
    isExpanded,
    isSpam,
    isPredicted,
    isPredictedToCurrentProject,
    attributes,
    importance: entry.importance || 0,
    groupDuration: options.groupDuration ?? calculateDuration(0),
    searchString: `${entry.title}${entry.type}${entry.description}`,
    _tic_source: !!entry._tic_source,
    cacheBuster: [
      entry.id,
      projectReference.id,
      isChecked,
      isPredictedToCurrentProject,
      isLogged,
      isExpanded,
      isSpam,
      entry.to,
      subEntries.length,
    ].join(),
    tic: entry.tic,
  };
};

function getStatusMessage({
  isLogged,
  numEntries,
  numSelectedEntries,
}: {
  isLogged: boolean;
  numEntries: number;
  numSelectedEntries: number;
}) {
  if (isLogged || numEntries === 1) {
    return null;
  }

  if (numSelectedEntries === 0) {
    return `${numEntries} ${numEntries === 1 ? 'memory' : 'memories'}`;
  }

  return `${numSelectedEntries}/${numEntries} memories`;
}

interface GroupMemoriesFactoryOptions {
  importanceMinThreshold: number;
  expandGroupRefs: Array<string>;
  expandedEntryIds: Array<number>;
  activeProjectId: number | null;
  operatorMode: OperatorMode;
  filterUncheckedMemories?: boolean;
}
export const groupMemoriesFactory = (
  groups: Array<SuggestedEntryWithProjects> = [],
  allMemories: Array<Memory> = [],
  options: GroupMemoriesFactoryOptions = {
    importanceMinThreshold: 0,
    expandGroupRefs: [],
    expandedEntryIds: [],
    activeProjectId: null,
    operatorMode: OperatorMode.withHourDraft,
    filterUncheckedMemories: false,
  }
): MemoryGroups => {
  const constructedGroups: MemoryGroups = groups
    .filter(group => group.entries.length > 0)
    .map(group => {
      const memories: Array<Memory> = allMemories.filter(memory => {
        return group.entry_ids.includes(memory.id);
      });
      const numEntries = memories.length;
      const groupedEntries = memories.reduce(
        (
          prev: {
            selectedEntries: Array<Memory>;
            unSelectedEntries: Array<Memory>;
            loggedEntries: Array<Memory>;
          },
          curr
        ) => {
          if (curr.isChecked) {
            prev.selectedEntries.push(curr);
          }

          if (!curr.isChecked) {
            prev.unSelectedEntries.push(curr);
          }
          if (curr.isLogged) {
            prev.loggedEntries.push(curr);
          }
          return prev;
        },
        { selectedEntries: [], unSelectedEntries: [], loggedEntries: [] }
      );

      const { loggedEntries, selectedEntries, unSelectedEntries } = groupedEntries;
      const numSelectedEntries = selectedEntries.length;
      const numLoggedEntries = loggedEntries.length;
      const isPredicted = memories.some(entry => entry.isPredicted);
      const isLogged = numEntries === numLoggedEntries && numLoggedEntries > 0;
      const isPartlyLogged = !isLogged && numLoggedEntries > 0;
      const isChecked = numEntries === numSelectedEntries && numEntries !== 0;
      // Warning: We're treating the whole group as spammed if any one of it's entries are marked.
      // This might lead to weird confusion later on, as it's patching server-side inconsistencies while AI signals are updated
      const isSpam = memories.some(entry => entry.isSpam);
      const isPartlyChecked = !isChecked && numSelectedEntries > 0;
      const isExpanded = options.expandGroupRefs.includes(group.ref);

      const predictionForCurrentProject = options.activeProjectId
        ? group.projects.find(({ base: { id } }) => id === options.activeProjectId)
        : null;

      const currentProjectPrediction = predictionForCurrentProject
        ? predictionForCurrentProject.accuracy
        : 0;

      const statusMessage = getStatusMessage({
        isLogged,
        numEntries,
        numSelectedEntries,
      });

      const durationForSelected = combinedDurationOfMemories(selectedEntries);
      const durationForUnselected = combinedDurationOfMemories(unSelectedEntries);

      // When logged, show actual duration on Memory Group, otherwise
      // show the time adjusted for overlap/collision with other memories
      const showCalculatedDuration = isLogged
        ? false
        : durationForSelected.total_seconds > 0 || durationForUnselected.total_seconds > 0;

      const showDuration = !showCalculatedDuration && group.duration.total_seconds > 0;
      const showTimestamp = !showCalculatedDuration && group.duration.total_seconds === 0;
      const hasMemories = memories.length > 0;
      const importance = group.importance;

      const hasAtleastOnePredictedProject = !!group.projects.length && !!group.projects[0];
      const primaryProject = !hasAtleastOnePredictedProject
        ? null
        : {
            title: group.projects[0].base.name,
            color: `#${group.projects[0].base.color}`,
          };

      const showPrediction = isPredicted || isLogged || isPartlyLogged;

      let filteredMemories = memories;
      if (options.filterUncheckedMemories) {
        filteredMemories = filteredMemories.filter(memory => memory.isChecked);
      }

      let icon = group.icon_url;
      const fallbackIcon = group.icon_fallback_url;

      for (const entry of group.entries) {
        const attribute = entry.extra_attributes?.find(a => a.name === 'application');
        if (attribute) {
          const application = encodeURIComponent(attribute.value);
          icon = `https://icons.timelyapp.com/apps/${getIconPlatform(
            entry.type
          )}/${application}?size=64&fallback=${encodeURIComponent(icon)}`;

          break;
        }
      }

      const url = memories.find(memory => memory.url)?.url;
      if (url != null) {
        try {
          const hostname = new URL(url).hostname;
          if (hostname) {
            icon = `https://icons.timelyapp.com/favicons/${hostname}?size=64&fallback=${group.icon_url}`;
          }
        } catch (e) {}
      }

      return {
        ref: group.ref,
        title: group.title,
        subtitle: group.description || group.note,
        duration: group.duration,
        durationForSelected,
        durationForUnselected,
        icon,
        fallbackIcon,
        url: null,
        importance,
        importancePercent: `${Math.ceil(importance * 100)}%`,
        currentProjectPrediction,
        currentProjectPredictionPercent: `${Math.ceil(currentProjectPrediction * 100)}%`,
        isSpam,
        isLogged,
        isPartlyLogged,
        isChecked,
        isPartlyChecked,
        isPredicted,
        isExpanded,
        memories: filteredMemories,
        projects: group.projects,
        primaryProject,
        ui: {
          hasMemories,
          showPrediction,
          showCalculatedDuration,
          showDuration,
          showTimestamp, // rename: showEntryCount or something
        },
        status: {
          isVisible: !!statusMessage,
          variant:
            numSelectedEntries === 0 || isChecked ? ('default' as const) : ('emphasis' as const),
          message: statusMessage,
        },
      };
    })
    .filter(group => group.memories.length > 0);

  return orderBy(
    constructedGroups,
    [group => group.isSpam, group => group.duration.total_seconds],
    ['asc', 'desc']
  );
};

function getIconPlatform(type?: string) {
  const lowercasedType = type?.toLowerCase();
  if (lowercasedType === 'macos') {
    return 'mac';
  }
  if (lowercasedType === 'windows') {
    return 'windows';
  }
  return 'linux';
}

// ! Lifted from Eric' code challenge: We must check with him before using / pay him
interface GroupMemoriesInTimelineLanesOptions {
  /** Minimum space between vertical memories, to avoid overlapping */
  minSpaceBetweenMemoriesInHours: number;
  /** Set a minimum hour duration for memories -- helps for dot apps with 0 second duration */
  minMemoryDurationInHours: number;
  sortBy: TimelineSortTypes;
}

function placeMemoriesInTimelineByTime(
  memories: Array<Memory> = [],
  options: GroupMemoriesInTimelineLanesOptions
) {
  const sortedByTime = orderBy(
    memories,
    [memory => memory.duration.total_seconds, memory => +new Date(memory.from)],
    ['desc', 'asc']
  );
  return placeMemoriesIntoLanesInOrder(sortedByTime, options);
}

function placeMemoriesInTimelineByAiImportance(
  memories: Array<Memory> = [],
  options: GroupMemoriesInTimelineLanesOptions
) {
  const sortedByImportance = orderBy(memories, [memory => memory.importance], ['desc']);
  return placeMemoriesIntoLanesInOrder(sortedByImportance, options);
}

function placeMemoriesInTimelineByGroupDuration(
  memories: Array<Memory> = [],
  options: GroupMemoriesInTimelineLanesOptions
) {
  const sortedByGroupDuration = orderBy(
    memories,
    [
      memory => memory.groupDuration.total_seconds,
      memory => memory.duration.total_seconds,
      memory => +new Date(memory.from),
    ],
    ['desc', 'desc', 'asc']
  );
  return placeMemoriesIntoLanesInOrder(sortedByGroupDuration, options);
}

function placeMemoriesIntoLanesInOrder(
  memories: Array<Memory>,
  options: GroupMemoriesInTimelineLanesOptions
): Array<Array<Memory>> {
  const lanes: Array<Array<MemoryAndLayoutData>> = [[]];

  memories.forEach(memory => {
    const fromSeconds = +Date.parse(memory.from) / 1000;
    const toSeconds = +Date.parse(memory.to) / 1000;
    const memoryWithOffset: MemoryAndLayoutData = {
      memory,
      offset: fromSeconds,
      length: Math.max(options.minMemoryDurationInHours * 60 * 60, toSeconds - fromSeconds),
    };
    lanes.some((lane, i) => {
      const isLastLane = i === lanes.length - 1;
      const hasOverlappingMemory = lane.some((existingMemory: MemoryAndLayoutData) => {
        return memoriesOverlapWithPadding(
          memoryWithOffset,
          existingMemory,
          options.minSpaceBetweenMemoriesInHours * 60 * 60
        );
      });
      if (!hasOverlappingMemory) {
        lane.push(memoryWithOffset);
        return true;
      } else if (hasOverlappingMemory && isLastLane) {
        const newLane = [memoryWithOffset];
        lanes.push(newLane);
        return true;
      }
      return false;
    });
  });
  return lanes.map(lane => lane.map(({ memory }) => memory));
}
interface MemoryAndLayoutData {
  memory: Memory;
  offset: number;
  length: number;
}
const memoriesOverlapWithPadding = (
  e1: { offset: number; length: number },
  e2: { offset: number; length: number },
  padding: number
): boolean => {
  if (e2.offset - padding <= e1.offset && e1.offset <= e2.offset + e2.length + padding) {
    return true;
  }
  if (e1.offset - padding <= e2.offset && e2.offset <= e1.offset + e1.length + padding) {
    return true;
  }
  return false;
};

export const placeMemoriesInTimelineLanes = (
  memories: Array<Memory> = [],
  options: GroupMemoriesInTimelineLanesOptions
): Array<Array<Memory>> => {
  if (!memories.length) {
    return [];
  } // Abort if no memories exist

  if (options.sortBy === TimelineSortTypes.sortByTime) {
    return placeMemoriesInTimelineByTime(memories, options);
  }

  if (options.sortBy === TimelineSortTypes.sortByAiImportance) {
    return placeMemoriesInTimelineByAiImportance(memories, options);
  }

  return placeMemoriesInTimelineByGroupDuration(memories, options);
};

/**
 * Places memories into their respective lanes.
 */

export const generateMemoryTimelineFactory = (
  memories: Array<Memory> = [],
  options: GroupMemoriesInTimelineLanesOptions
): Array<Array<Memory>> => {
  if (!memories.length) {
    return [];
  } // Abort if no memories exist

  const { gpsLane, otherMemories } = memories.reduce(
    (value, memory) => {
      const isGPSMemory = memory.type === TIMELY_GPS_TRACKER;
      if (isGPSMemory) {
        value.gpsLane.push(memory);
      } else {
        value.otherMemories.push(memory);
      }
      return value;
    },
    {
      gpsLane: [],
      otherMemories: [],
    } as {
      gpsLane: Array<Memory>;
      otherMemories: Array<Memory>;
    }
  );
  // Place the remaining memories
  const otherMemoryLanes = placeMemoriesInTimelineLanes(otherMemories, options);

  if (gpsLane.length) {
    return [orderBy(gpsLane, memory => +new Date(memory.from), 'asc'), ...otherMemoryLanes];
  }
  return otherMemoryLanes;
};

export type MemoryEmptyStates =
  | 'loading-memories' // only TIC supported
  | 'no-memory-apps'
  | 'no-memories-on-day'
  | 'no-memories-on-day-in-future'
  | 'inbox-zero'
  | 'inbox-zero-with-filters';

interface MemoryEmptyStateMeta {
  hasAppConnected: boolean;
  isEmpty: boolean;
  hasSelectedAllMemories: boolean;
  totalMemories: number;
  isFiltering: boolean;
  date: string;
}

export const memoryEmptyStateFactory = (meta: MemoryEmptyStateMeta): MemoryEmptyStates => {
  const { hasAppConnected, isEmpty, hasSelectedAllMemories, date, totalMemories, isFiltering } =
    meta;

  if (!hasAppConnected) {
    return 'no-memory-apps';
  }

  if (hasAppConnected && isEmpty && totalMemories === 0) {
    if (isAfter(date, DateTime.now().toISODate())) {
      return 'no-memories-on-day-in-future';
    }
    return 'no-memories-on-day';
  }

  if (hasSelectedAllMemories) {
    return 'inbox-zero';
  }

  if ((hasAppConnected && isEmpty && totalMemories > 0) || (totalMemories > 0 && isFiltering)) {
    return 'inbox-zero-with-filters';
  }

  return 'inbox-zero';
};

export function getDatesForMiniTimeline(startDate: string, numberOfHours: number) {
  let currentDate: DateTime<true> = fromISO(startDate).startOf('hour');
  const dateArray: Array<DateTime<true>> = [];
  let i = 0;
  while (i < numberOfHours) {
    i++;
    dateArray.push(currentDate);
    currentDate = currentDate.plus({ hour: 1 });
  }
  return dateArray;
}
